/**
 * Helpers puros do filtro/busca do Kanban (storage, predicados, queries org-search).
 */

import { Lead, Task } from '../../../types';
import { Filters } from '../../../components/shared/filters/types';
import { supabase } from '../../../lib/supabase';
import { LeadMapper } from '../../../infrastructure/repositories/mappers/LeadMapper';
import { mapDomainLeadToFrontend, noopLeadHistoryCache } from '../../../contexts/leads/utils/leadMappers';

export const SEARCH_KEY = 'kanban_search_term';
export const FILTERS_KEY = 'kanban_filters';
export const GLOBAL_STAGES = new Set(['all', 'won', 'lost']);

const OPP_SELECT = '*, contact:contacts(name, email, whatsapp, phone)';
const UNASSIGNED = '__unassigned__';
const DEFAULT_FILTERS: Filters = {
    stage: 'all', tags: [], dateRange: { start: null, end: null }, agents: [],
    noTasks: false, overdueTasks: false, statusWon: false, statusLost: false,
};

type ContactColumn = 'name' | 'email' | 'phone' | 'whatsapp';

export const hasWindow = () => typeof window !== 'undefined';
export const sanitizeIlike = (raw: string) => raw.trim().replace(/[%_]/g, '').slice(0, 80);
const dayTs = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

export const toLead = (row: unknown): Lead =>
    mapDomainLeadToFrontend(LeadMapper.toEntity(row as Record<string, unknown>), noopLeadHistoryCache);

export function readStoredSearch(): string {
    try { return hasWindow() ? (sessionStorage.getItem(SEARCH_KEY) ?? '') : ''; }
    catch { return ''; }
}

export function readStoredFilters(): Filters {
    if (!hasWindow()) return DEFAULT_FILTERS;
    try {
        const saved = localStorage.getItem(FILTERS_KEY);
        if (!saved) return DEFAULT_FILTERS;
        const parsed = JSON.parse(saved);
        if (parsed.dateRange) {
            parsed.dateRange.start = parsed.dateRange.start ? new Date(parsed.dateRange.start) : null;
            parsed.dateRange.end = parsed.dateRange.end ? new Date(parsed.dateRange.end) : null;
        }
        return parsed;
    } catch (e) {
        console.error('Erro ao ler filtros do storage', e);
        return DEFAULT_FILTERS;
    }
}

/**
 * Member não tem UI de responsável, mas `kanban_filters` no localStorage é global.
 * Filtros stale (`agents` / `noAgent`) esvaziam o board sem como limpar na tela.
 */
export function sanitizeFiltersForViewer(filters: Filters, canFilterByAgent: boolean): Filters {
    if (canFilterByAgent) return filters;
    if (!filters.noAgent && filters.agents.length === 0) return filters;
    return { ...filters, agents: [], noAgent: false };
}

function matchesStatus(lead: Lead, f: Filters): boolean {
    const status = lead.status || 'open';
    if (f.stage === 'won') return status === 'won';
    if (f.stage === 'lost') return status === 'lost';
    if (f.stage !== 'all') return status === 'open' && lead.stage === f.stage;
    if (f.statusWon || f.statusLost) {
        if (status === 'won') return !!f.statusWon;
        if (status === 'lost') return !!f.statusLost;
        return false;
    }
    return status === 'open';
}

function matchesSearch(lead: Lead, term: string): boolean {
    const q = term.toLowerCase().trim();
    if (!q) return true;
    const digits = q.replace(/\D/g, '');
    const wa = (lead.whatsApp || '').replace(/\D/g, '');
    return lead.name.toLowerCase().includes(q)
        || (!!lead.email && lead.email.toLowerCase().includes(q))
        || (digits.length >= 3 && wa.includes(digits));
}

function matchesAgent(lead: Lead, f: Filters): boolean {
    if (f.noAgent) return !lead.agent;
    if (f.agents.length === 0) return true;
    const wantsNone = f.agents.includes(UNASSIGNED);
    const ids = f.agents.filter((a) => a !== UNASSIGNED);
    return (wantsNone && !lead.agent) || (!!lead.agent && ids.includes(lead.agent));
}

function matchesTasks(lead: Lead, f: Filters, tasks: Task[]): boolean {
    if (f.noTasks && tasks.some((t) => t.leadId === lead.id)) return false;
    if (!f.overdueTasks) return true;
    const today = dayTs(new Date());
    return tasks.some((t) =>
        t.leadId === lead.id && !t.isCompleted && !!t.dueDate && dayTs(new Date(t.dueDate)) < today
    );
}

export function matchesLead(lead: Lead, f: Filters, term: string, tasks: Task[]): boolean {
    if (!matchesStatus(lead, f) || !matchesSearch(lead, term)) return false;
    if (f.tags.length > 0 && !lead.tags?.some((t) => f.tags.includes(t))) return false;
    const leadDay = dayTs(new Date(lead.entryDate));
    if (f.dateRange.start && leadDay < dayTs(f.dateRange.start)) return false;
    if (f.dateRange.end && leadDay > dayTs(f.dateRange.end)) return false;
    return matchesAgent(lead, f) && matchesTasks(lead, f, tasks);
}

async function fetchContactIds(
    orgId: string, column: ContactColumn, pattern: string, limit: number,
): Promise<{ id: string }[]> {
    const { data } = await supabase.from('contacts').select('id')
        .eq('organization_id', orgId).ilike(column, pattern).limit(limit);
    return data || [];
}

export async function collectContactIds(
    orgId: string,
    queries: Array<{ column: ContactColumn; pattern: string; limit: number }>,
): Promise<Set<string>> {
    const batches = await Promise.all(
        queries.map(({ column, pattern, limit }) => fetchContactIds(orgId, column, pattern, limit)),
    );
    return new Set(batches.flat().map((c) => c.id));
}

export async function fetchOppsInStages(
    orgId: string,
    stageIds: string[],
    opts: { titlePattern?: string; contactIds?: string[]; limit?: number },
) {
    let query = supabase.from('opportunities').select(OPP_SELECT)
        .eq('organization_id', orgId).in('stage', stageIds).limit(opts.limit ?? 80);
    if (opts.titlePattern) query = query.ilike('title', opts.titlePattern);
    if (opts.contactIds?.length) query = query.in('contact_id', opts.contactIds);
    return query;
}
